import { FolderNode, FolderType } from 'types/sequences';
import 'twin.macro';
import React from 'react';
import { Breadcrumb } from '../Breadcrumb';
import { getLinkOfFolderNode } from '../urls';
import { FolderSequenceIcon } from '../FolderSequenceIcon';
import { CreateButton } from './CreateButton';
import { StackList } from 'components/StackList';
import { useNavigate } from 'react-router-dom';
import { useUpdateSequencesFolders } from 'queries/sequences/useUpdateSequencesFolders';
import { PageWrapper } from '../components/PageWrapper';
import { SequenceMobileSubHeader } from '../components/SequenceMobileSubheader';
import { useGetMenuBasedOnFolderType } from '../components/useGetMenuBasedOnFolderType';
import { useTranslation } from 'react-i18next';
import { InlineEditableTitle } from '../components/UpdateFolderTitleModal/InlineEditableTitle';
import { UpdateFolderTitleModal } from '../components/UpdateFolderTitleModal';

interface Props {
  folder: FolderNode;
  ancestors?: { id: string; title: string; type: FolderType }[];
  onChange?: (v: FolderNode) => any;
  home?: boolean;
}

export function FolderContent(props: Props) {
  const { folder, ancestors, home } = props;
  const navigate = useNavigate();
  const updateSequencePath = useUpdateSequencesFolders();
  const getMenuItems = useGetMenuBasedOnFolderType();
  const { t } = useTranslation(['sequence']);

  return (
    <PageWrapper>
      <div tw="hidden my-2 px-6 md:flex justify-between items-center">
        {home ? (
          <div tw="h-14 -mt-1" />
        ) : (
          <Breadcrumb
            folder={folder}
            ancestors={ancestors}
            onChange={props.onChange}
          />
        )}
        <div>
          <CreateButton data-testid="create-button" folderId={folder.id} />
        </div>
      </div>
      <SequenceMobileSubHeader folder={folder} ancestors={ancestors}>
        {home ? (
          <div tw="h-14 -mt-1" />
        ) : (
          <Breadcrumb
            folder={folder}
            ancestors={ancestors}
            onChange={props.onChange}
            mobile
          />
        )}
        <CreateButton data-testid="create-button-mobile" folderId={folder.id} />
      </SequenceMobileSubHeader>
      <PageWrapper.Body>
        <InlineEditableTitle
          id={folder.id}
          title={home ? t('sequence:summary') : folder.title}
          onChange={
            home
              ? undefined
              : v => {
                  if (!props.onChange) {
                    return;
                  }
                  return props.onChange({
                    ...folder,
                    title: v,
                  });
                }
          }
        />

        <StackList<FolderNode>
          onDragEnd={async ({ item, overId }) => {
            if (
              item.type !== 'sequence' &&
              item.type !== 'folder' &&
              item.type !== 'fdp_standalone'
            ) {
              return;
            }
            const matchingOver = (folder?.children || []).find(
              f => f.id === overId,
            );
            if (!matchingOver) {
              return;
            }
            if (matchingOver.type === 'sequence') {
              return;
            }
            await updateSequencePath({
              items: [
                {
                  id: item.id,
                  destFolderId: overId,
                  type: item.type,
                },
              ],
            });
          }}
          onClick={item => navigate(getLinkOfFolderNode(item, ancestors || []))}
          actionsFct={item => getMenuItems(item)}
          items={(folder?.children || []).map(f => ({
            id: f.id,
            icon: <FolderSequenceIcon type={f.type} tw="h-8 w-8" />,
            title: f.title || t(`sequence:fileTree.defaultLabel.${f.type}`),
            isFolder: ['folder'].includes(f.type),
            type: f.type,
            disabledOver: () => f.type === 'sequence',
          }))}
        />
      </PageWrapper.Body>
      <UpdateFolderTitleModal />
    </PageWrapper>
  );
}
